import { useState } from "react"
import { z } from "zod"

import { pro } from "@productlane/lib"
import {
	Alert,
	Button,
	Form,
	FormInput,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { UpgradeButton } from "@/components/upgrade-button"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useTrial } from "@/lib/useTrial"
import { PlanPill } from "@/screens/settings/plan-pill"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsHeader } from "../../settings-header"

const FormSchema = z.object({
	apiKey: z.string().min(1),
})

export function ProductboardImportPage() {
	const [state, setState] = useState<"error" | "validate" | "success">(
		"validate",
	)
	const { hasTrialEnded } = useTrial()
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

	const form = useZodForm({
		schema: FormSchema,
	})
	const validateDataMutation = api.productboard.validateData.useMutation({
		onSuccess: () => {
			setState("validate")
		},
		onError: () => {
			setState("error")
		},
	})
	const executeImportMutation = api.productboard.executeImport.useMutation({
		onSuccess: () => {
			setState("success")
			form.reset()
		},
		onError: () => {
			setState("error")
		},
	})
	return (
		<>
			<SettingsHeader
				title="Productboard import"
				subtitle={null}
				titleSuffix={<PlanPill plan="pro" />}
			/>
			{!hasTrialEnded ? (
				<SettingsContainer>
					<div className="text-sm text-secondary">
						To get started with the Productboard import,{" "}
						<a
							href="https://developer.productboard.com/reference/authentication"
							target="_blank"
							rel="noreferrer"
							className="font-semibold underline"
						>
							please create an API key
						</a>
						. We won&apos;t store the API key in our system and use it only for
						the import.
					</div>

					<Form
						className="pt-4"
						form={form}
						onSubmit={async (values) => {
							await validateDataMutation.mutateAsync({
								apiKey: values.apiKey,
							})
						}}
					>
						<FormInput
							type="password"
							name="apiKey"
							placeholder="eyJ0eXAiOiJK..."
							label="API Key"
						/>
						<div className="mt-2 flex items-center justify-between gap-4">
							<div className="text-sm text-secondary">
								Validate API key and do a dry run of the import to detect any
								data that might be missing for the import.
							</div>
							{hasRequiredPlan ? (
								<SubmitButton
									onClick={() => {
										setState("validate")
									}}
								>
									Validate data
								</SubmitButton>
							) : (
								<UpgradeButton required={pro.andUp} />
							)}
						</div>
					</Form>

					{state === "error" && (
						<Alert
							header="Something went wrong"
							variant="error"
							className="mt-10"
						>
							We have been notified and will investigate why the import failed.
						</Alert>
					)}

					{validateDataMutation.data && state === "validate" && (
						<>
							<Alert header="Validate data" className="mt-10">
								Make sure all the data looks good before running the import.
								Click on &quot;Validate data&quot; to run the validation again.
							</Alert>

							<div className="mt-10 text-secondary">
								<div className="mb-6 font-medium text-primary">
									Dry run results
								</div>
								<div className="font-medium">
									Users without an email will be skipped during the import.
								</div>
								<div>
									Found {validateDataMutation.data?.contactsWithoutEmail.length}{" "}
									users without an email. Please add an email for the users you
									wish to import.
								</div>
								{validateDataMutation.data?.contactsWithoutEmail.length ? (
									<ul className="mb-6 mt-2 list-inside list-disc">
										{validateDataMutation.data?.contactsWithoutEmail.map(
											(contact) => (
												<li key={contact.id}>{contact.name}</li>
											),
										)}
									</ul>
								) : null}

								<div className="font-medium">
									Companies without a domain will be skipped during the import.
								</div>
								<div>
									Found{" "}
									{validateDataMutation.data?.companiesWithoutDomain.length}{" "}
									companies without a domain. Please add a domain for the
									companies you wish to import.
								</div>
								{validateDataMutation.data?.companiesWithoutDomain.length ? (
									<ul className="mb-6 mt-2 list-inside list-disc">
										{validateDataMutation.data?.companiesWithoutDomain.map(
											(company) => (
												<li key={company.id}>{company.name}</li>
											),
										)}
									</ul>
								) : null}

								<div className="mb-6 font-medium">
									{validateDataMutation.data?.featuresWithNotes} features,{" "}
									{validateDataMutation.data?.componentsWithNotes} components,{" "}
									{validateDataMutation.data?.productsWithNotes} products will
									be imported as tags (you can convert tags to Linear
									issues/projects later).
								</div>
								<div className="font-medium">
									{validateDataMutation.data?.amountNotes} notes will be
									imported.
								</div>

								<div className="mt-6 flex justify-end">
									<Button
										onClick={() => {
											executeImportMutation.mutate({
												apiKey: form.getValues().apiKey,
											})
										}}
										isLoading={executeImportMutation.isLoading}
									>
										Run import
									</Button>
								</div>
							</div>
						</>
					)}

					{state === "success" && (
						<Alert header="Import complete" className="mt-10" variant="success">
							Everything was imported successfully.
						</Alert>
					)}
				</SettingsContainer>
			) : (
				<Alert variant="warning">Trial ended. Upgrade to import data.</Alert>
			)}
		</>
	)
}
